package com.flowyride.core.api

import kotlinx.serialization.Serializable

/**
 * HTTP contract between the FlowyRide apps and the FlowyRide server.
 * Shared by both sides: changing it breaks the build of whichever side is out of date.
 */
object RoutesApi {
    /** POST [RouteRequestDto] → 200 [RouteResponseDto], or an error status with [ApiErrorDto]. */
    const val PATH = "/api/routes"

    /** POST [TrackGuidanceRequestDto] → 200 [TrackGuidanceResponseDto]: instructions along an imported line. */
    const val GUIDANCE_PATH = "/api/routes/guidance"

    /** Longest line accepted by [GUIDANCE_PATH]. */
    const val GUIDANCE_MAX_POINTS = 100_000
}

@Serializable
data class TrackGuidanceRequestDto(val track: List<GeoPointDto>)

/** Indices point into the request's track. */
@Serializable
data class TrackGuidanceResponseDto(
    val maneuvers: List<ManeuverDto>,
    val speedLimits: List<SpeedLimitSectionDto> = emptyList(),
)

@Serializable
data class GeoPointDto(val lat: Double, val lon: Double)

@Serializable
enum class LevelDto { LOW, NORMAL, HIGH }

@Serializable
data class RoutePreferencesDto(
    val windingness: LevelDto,
    val hilliness: LevelDto,
    val avoidMotorways: Boolean,
)

@Serializable
enum class RouteStyleDto { WINDING, FASTEST }

@Serializable
data class RouteRequestDto(
    val waypoints: List<GeoPointDto>,
    val preferences: RoutePreferencesDto,
    val style: RouteStyleDto = RouteStyleDto.WINDING,
    /** Degrees, 0 = north; null when unknown. */
    val heading: Double? = null,
)

/** Elevation is optional: omitted when unknown. */
@Serializable
data class TrackPointDto(val lat: Double, val lon: Double, val ele: Double? = null)

@Serializable
data class RouteLegDto(
    val distanceMeters: Double,
    val durationSeconds: Double,
    val lastTrackIndex: Int,
)

/**
 * [type] is a [com.flowyride.core.domain.model.ManeuverType] name, kept as text: an app older than the server
 * reads a new kind as OTHER instead of failing.
 */
@Serializable
data class ManeuverDto(
    val type: String,
    val trackIndex: Int,
    val roadName: String? = null,
    val roadNumber: String? = null,
    val roundaboutExit: Int? = null,
    val turnAngle: Double? = null,
)

@Serializable
data class SpeedLimitSectionDto(val startTrackIndex: Int, val endTrackIndex: Int, val speedLimitKmh: Int)

@Serializable
data class RouteResponseDto(
    val engine: String,
    val distanceMeters: Double,
    val durationSeconds: Double,
    val track: List<TrackPointDto>,
    val legs: List<RouteLegDto>,
    val maneuvers: List<ManeuverDto> = emptyList(),
    val speedLimits: List<SpeedLimitSectionDto> = emptyList(),
)

@Serializable
data class ApiErrorDto(val code: ApiErrorCode, val message: String)

@Serializable
enum class ApiErrorCode {
    NO_ROUTE,
    TOO_MANY_WAYPOINTS,
    INVALID_REQUEST,
    ENGINE_UNAVAILABLE,
    UPSTREAM_ERROR,
    RATE_LIMITED,

    // Accounts
    UNAUTHORIZED,
    FORBIDDEN,
    INVALID_CREDENTIALS,
    ACCOUNT_PENDING,
    ACCOUNT_REFUSED,
    USERNAME_TAKEN,
    EMAIL_TAKEN,
    NOT_FOUND,
}
